import { FC, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useDispatch } from "react-redux";
import {
  useGetCronofyTimeZoneList,
  useGetUserCronofyServiceAccountDataQuery,
} from "../../../../api/services/manageUser/manageUserApi";
import {
  AccordionHeader,
  AccordionHeaderText,
  FormController,
  MuiBox,
  MuiGrid,
  MuiMultiSelect,
  MuiSelect,
  MuiTextField,
} from "../../../../components";
import {
  HOURS_LIST,
  MAX_LENGTH_100,
  MAX_LENGTH_1024,
  MAX_LENGTH_3000,
  MAX_LENGTH_500,
  WEEK_DAYS,
} from "../../../../helpers/constants";
import {
  convertStringArrayToOptions,
  getValueOrBlank,
  isNotEmpty,
} from "../../../../helpers/utils";
import { useGridContainer } from "../../../../hooks/useGridContainer";
import { useMultiSelectARIALabels } from "../../../../hooks/useMultiSelectARIALabels";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { useToast } from "../../../../hooks/useToast";
import { toggleLoader } from "../../../../redux/slices/appState/appStateSlice";
import { sortDaysByWeekOrder } from "../../helper";
import {
  CoachDetailKey,
  CoachTimeZoneType,
  createUserCoachDetailFormDefaultValues,
  ServiceAccountType,
  ViewUserDataType,
} from "../../modelsAndSchemas/models";
import { createUserSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { CreateUserCoachDetailWrapper } from "./CreateUserCoachDetail.style";

interface CreateUserCoachDetailProps {
  pageId: string;
  editUserData: ViewUserDataType;
  isEditUser: boolean;
  countryCode: string;
  isCoachAdmin?: boolean;
  availableDayRef: React.RefObject<{
    resetValues: (values?: string[]) => void;
  }>;
  isCoachResetConfirmed?: boolean;
}

export const CreateUserCoachDetail: FC<CreateUserCoachDetailProps> = ({
  pageId,
  countryCode,
  editUserData,
  isEditUser,
  isCoachAdmin,
  availableDayRef,
  isCoachResetConfirmed,
}) => {
  const dispatch = useDispatch();
  const { displayToast } = useToast();

  const {
    control,
    reset,
    setValue,
    getValues,
    formState: { isDirty: isDirtyCoachDetail },
  } = useFormContext();
  const { commonLabels, fetchLabel, commonValidationMessages } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const multiSelectARIALabels = useMultiSelectARIALabels();
  const { gridSizeMedium: gridSize, gridSizeMediumClass } =
    useGridContainer(true);
  const [isCountryReset, setIsCountryReset] = useState(false);

  const {
    data: serviceAccountList,
    isLoading: isServiceAccountListLoading,
    isError: isServiceAccountListError,
  } = useGetUserCronofyServiceAccountDataQuery();

  const {
    data: timeZoneList,
    refetchWithParams: refetchTimeZoneList,
    isFetching: isTimeZoneListLoading,
    isError: isTimeZoneListError,
  } = useGetCronofyTimeZoneList();

  useEffect(() => {
    if (isNotEmpty(countryCode)) {
      isCountryReset && setValue(CoachDetailKey.timeZone, "");
      refetchTimeZoneList({
        countryCode: countryCode,
      });
      setIsCountryReset(true);
    }
  }, [countryCode]);

  const timeZoneListOptions = useMemo(() => {
    if (isNotEmpty(timeZoneList)) {
      return timeZoneList.map((timeZone: CoachTimeZoneType) => ({
        label: timeZone.ianaTimeZoneIdentifier,
        value: timeZone.ianaTimeZoneIdentifier,
      }));
    }
    return [];
  }, [timeZoneList]);

  useEffect(() => {
    if (isServiceAccountListError || isTimeZoneListError) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isServiceAccountListError, isTimeZoneListError]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isServiceAccountListLoading || isTimeZoneListLoading,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isServiceAccountListLoading, isTimeZoneListLoading]);

  const getCoachDetailLabel = (key: string) =>
    fetchLabel(createUserSiteCoreKeys.coachDetailsKey, key);

  const serviceAccountOptions = useMemo(() => {
    if (isNotEmpty(serviceAccountList)) {
      return serviceAccountList.map((item: ServiceAccountType) => ({
        label: item.serviceAccountName,
        value: item.id,
      }));
    }
    return [];
  }, [serviceAccountList]);

  useEffect(() => {
    if (isCoachAdmin && !isDirtyCoachDetail) {
      reset(createUserCoachDetailFormDefaultValues);
      availableDayRef.current?.resetValues([]);
    }
  }, [isCoachAdmin]);

  //edit user data handling
  useEffect(() => {
    if (
      isEditUser &&
      isNotEmpty(editUserData) &&
      (!isDirtyCoachDetail || isCoachResetConfirmed)
    ) {
      const weeklyPeriodsDetails =
        editUserData?.schedulingAvailability?.weekly_periods ?? [];

      const availableDays = sortDaysByWeekOrder(
        weeklyPeriodsDetails
          .map((period) => period.day)
          .filter((day): day is string => day !== undefined),
      );

      reset({
        ...createUserCoachDetailFormDefaultValues,
        [CoachDetailKey.bioData]: getValueOrBlank(editUserData?.bio),
        [CoachDetailKey.expertise]: getValueOrBlank(editUserData?.expertise),
        [CoachDetailKey.linkedInId]: getValueOrBlank(
          editUserData?.socialIdDetails,
        ),
        [CoachDetailKey.timeZone]: getValueOrBlank(
          editUserData?.schedulingAvailability?.tzid,
        ),
        [CoachDetailKey.serviceAccountName]: getValueOrBlank(
          editUserData?.serviceAccountId,
        ),
        [CoachDetailKey.workingHoursStartTime]: getValueOrBlank(
          weeklyPeriodsDetails?.[0]?.start_time,
        ),
        [CoachDetailKey.workingHoursEndTime]: getValueOrBlank(
          weeklyPeriodsDetails?.[0]?.end_time,
        ),
        [CoachDetailKey.coachingLanguages]: getValueOrBlank(
          editUserData?.language,
        ),
        [CoachDetailKey.availableDays]: availableDays,
      });
      availableDayRef.current?.resetValues(availableDays);
    } else if (!isCoachResetConfirmed && isDirtyCoachDetail) {
      const { availableDays, timeZone } = getValues();
      availableDayRef.current?.resetValues(availableDays);
      setValue(CoachDetailKey.timeZone, timeZone);
    }
  }, [editUserData]);

  const renderHeader = (label: string) => {
    return <AccordionHeaderText label={label} />;
  };

  return (
    <CreateUserCoachDetailWrapper>
      <AccordionHeader
        className="accordion-header"
        title={renderHeader(
          getCoachDetailLabel(createUserSiteCoreKeys.profileSectionTitle),
        )}
        isExpanded={true}
        showExpandCollapseIcon={false}
      >
        <MuiBox className="coach-detail-container">
          <MuiGrid container>
            <MuiGrid
              size={{ lg: 8, md: 12, xs: 12 }}
              className={gridSizeMediumClass}
              spacing={4}
            >
              <FormController control={control} name={CoachDetailKey.bioData}>
                <MuiTextField
                  id={`${pageId}-${CoachDetailKey.bioData}`}
                  label={getCoachDetailLabel(createUserSiteCoreKeys.bioData)}
                  placeholder={commonLabels.inputPlaceHolder}
                  isTextarea
                  rows={3}
                  maxLength={MAX_LENGTH_3000}
                  shouldTrimSpaces
                  requiredARIALabel={commonARIALabel.required}
                  multiline
                />
              </FormController>
            </MuiGrid>
          </MuiGrid>
          <MuiGrid container>
            <MuiGrid
              size={gridSize}
              className={`${gridSizeMediumClass} form-grid`}
            >
              <FormController control={control} name={CoachDetailKey.expertise}>
                <MuiTextField
                  id={`${pageId}-${CoachDetailKey.expertise}`}
                  label={getCoachDetailLabel(createUserSiteCoreKeys.expertise)}
                  placeholder={commonLabels.inputPlaceHolder}
                  shouldTrimSpaces
                  maxLength={MAX_LENGTH_1024}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
            <MuiGrid
              size={gridSize}
              className={`${gridSizeMediumClass} form-grid`}
            >
              <FormController
                control={control}
                name={CoachDetailKey.linkedInId}
              >
                <MuiTextField
                  id={`${pageId}-${CoachDetailKey.linkedInId}`}
                  label={getCoachDetailLabel(createUserSiteCoreKeys.linkedinId)}
                  placeholder={commonLabels.inputPlaceHolder}
                  shouldTrimSpaces
                  requiredARIALabel={commonARIALabel.required}
                  maxLength={MAX_LENGTH_500}
                />
              </FormController>
            </MuiGrid>
            <MuiGrid
              size={{ lg: 8, md: 12, xs: 12 }}
              className={gridSizeMediumClass}
              spacing={4}
            >
              <FormController
                control={control}
                name={CoachDetailKey.coachingLanguages}
              >
                <MuiTextField
                  id={`${pageId}-${CoachDetailKey.coachingLanguages}`}
                  label={getCoachDetailLabel(
                    createUserSiteCoreKeys.coachingLanguages,
                  )}
                  placeholder={commonLabels.inputPlaceHolder}
                  isTextarea
                  rows={3}
                  maxLength={MAX_LENGTH_100}
                  shouldTrimSpaces
                  requiredARIALabel={commonARIALabel.required}
                  isMandatory
                  tooltipMessage={
                    commonValidationMessages.fieldSpecialCharactersTooltipMessage
                  }
                  tooltipHeaderTitle={
                    commonValidationMessages.allowedSpecialCharactersTitle
                  }
                  multiline
                />
              </FormController>
            </MuiGrid>
          </MuiGrid>
        </MuiBox>
      </AccordionHeader>
      <AccordionHeader
        className="accordion-header"
        title={renderHeader(
          getCoachDetailLabel(createUserSiteCoreKeys.cronofySectionTitle),
        )}
        isExpanded={true}
        showExpandCollapseIcon={false}
      >
        <MuiBox className="cronofy-container">
          <MuiGrid container>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController control={control} name={CoachDetailKey.timeZone}>
                <MuiSelect
                  label={getCoachDetailLabel(
                    createUserSiteCoreKeys.coachTimeZone,
                  )}
                  options={timeZoneListOptions}
                  visibleDefaultSelectOption
                  value=""
                  id={`${pageId}-${CoachDetailKey.timeZone}`}
                  placeholder={commonLabels.selectPlaceHolder}
                  isMandatory
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController
                control={control}
                name={CoachDetailKey.serviceAccountName}
              >
                <MuiSelect
                  label={getCoachDetailLabel(
                    createUserSiteCoreKeys.serviceAccountName,
                  )}
                  options={serviceAccountOptions}
                  visibleDefaultSelectOption
                  value=""
                  id={`${pageId}-${CoachDetailKey.serviceAccountName}`}
                  placeholder={commonLabels.selectPlaceHolder}
                  isMandatory
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController
                control={control}
                name={CoachDetailKey.availableDays}
                ref={availableDayRef}
              >
                <MuiMultiSelect
                  label={getCoachDetailLabel(
                    createUserSiteCoreKeys.availableDays,
                  )}
                  options={WEEK_DAYS}
                  // options={fetchDropdownValues(
                  //   createUserSiteCoreKeys.coachDetailsKey,
                  //   createUserSiteCoreKeys.availableDaysList,
                  // )}
                  showSearch
                  showChip
                  isMandatory
                  id={`${pageId}-${CoachDetailKey.availableDays}`}
                  chipId={`${pageId}-${CoachDetailKey.availableDays}-chip`}
                  dialogTitle={getCoachDetailLabel(
                    createUserSiteCoreKeys.selectedAvailableDays,
                  )}
                  isDialogTitleWithEllipsis
                  selectPlaceHolder={commonLabels.selectPlaceHolder}
                  searchPlaceHolder={commonLabels.search}
                  selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
                  doneText={commonLabels.done}
                  allARIAProps={multiSelectARIALabels}
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController
                control={control}
                name={CoachDetailKey.workingHoursStartTime}
              >
                <MuiSelect
                  label={getCoachDetailLabel(
                    createUserSiteCoreKeys.workingHoursStartTime,
                  )}
                  options={convertStringArrayToOptions(HOURS_LIST)}
                  // options={fetchDropdownValues(
                  //   createUserSiteCoreKeys.coachDetailsKey,
                  //   createUserSiteCoreKeys.workingHoursStartTimeList,
                  // )}
                  visibleDefaultSelectOption
                  value=""
                  id={`${pageId}-${CoachDetailKey.workingHoursStartTime}`}
                  placeholder={commonLabels.selectPlaceHolder}
                  isMandatory
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController
                control={control}
                name={CoachDetailKey.workingHoursEndTime}
              >
                <MuiSelect
                  label={getCoachDetailLabel(
                    createUserSiteCoreKeys.workingHoursEndTime,
                  )}
                  options={convertStringArrayToOptions(HOURS_LIST)}
                  // options={fetchDropdownValues(
                  //   createUserSiteCoreKeys.coachDetailsKey,
                  //   createUserSiteCoreKeys.workingHoursEndTimeList,
                  // )}
                  visibleDefaultSelectOption
                  value=""
                  id={`${pageId}-${CoachDetailKey.workingHoursEndTime}`}
                  placeholder={commonLabels.selectPlaceHolder}
                  isMandatory
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
          </MuiGrid>
        </MuiBox>
      </AccordionHeader>
    </CreateUserCoachDetailWrapper>
  );
};
